import React, { useState, useEffect } from 'react';
import {
    ChevronLeft, Send, Trash2, MapPin, QrCode, Archive
} from 'lucide-react';

import { User, Ticket, Status, TimelineEvent } from '../../types';
import {
    getPriorityColor, getStatusColor, getStatusLabel, getRelativeTime,
    getSLAStatus, getSLAColor, formatSLATime
} from '../../utils/helpers';
import { Button } from '../shared';
import { FileUpload } from './FileUpload';
import { AttachmentService, Attachment } from '../../services/attachment.service';
import { QRGenerator } from '../verification/QRGenerator';

interface TicketDetailProps {
    ticket: Ticket;
    user: User;
    users: User[];
    onBack: () => void;
    onDelete: (id: string) => void;
    onUpdate: (ticket: Ticket) => void;
    onArchive?: (id: string) => void;
}

export const TicketDetail: React.FC<TicketDetailProps> = ({
    ticket,
    user,
    users,
    onBack,
    onDelete,
    onUpdate,
    onArchive
}) => {
    const [activeTab, setActiveTab] = useState('TIMELINE');
    const [comment, setComment] = useState('');
    const [newStatus, setNewStatus] = useState<Status>(ticket.status);
    const [assignee, setAssignee] = useState(ticket.assignedTo || '');
    const [attachments, setAttachments] = useState<Attachment[]>([]);
    const [showQRGenerator, setShowQRGenerator] = useState(false);

    const isAdmin = user.role === 'HELPDESK' || user.role === 'COORD' || user.role === 'SUPERVISOR';
    const isHelpdesk = user.role === 'HELPDESK' || user.role === 'COORD';
    const isOwner = user.role === 'STORE' && ticket.siteId === user.siteId;
    const canDelete = isAdmin || isOwner;
    const canEdit = isAdmin || isOwner || user.role === 'TECH';

    const techUsers = users.filter(u => u.role === 'TECH' || u.role === 'HELPDESK');
    const slaStatus = getSLAStatus(ticket.slaDue);

    // Load attachments when ticket changes
    useEffect(() => {
        const loadAttachments = async () => {
            const files = await AttachmentService.getAttachments(ticket.id);
            setAttachments(files);
        };
        loadAttachments();
    }, [ticket.id]);

    const handleAddComment = () => {
        if (!comment.trim()) return;

        const newEvent: TimelineEvent = {
            id: `E${Date.now()}`,
            userId: user.id,
            userName: user.name,
            type: 'COMMENT',
            content: comment,
            timestamp: new Date(),
        };

        const updatedTicket = {
            ...ticket,
            timeline: [...ticket.timeline, newEvent],
            updatedAt: new Date(),
        };

        onUpdate(updatedTicket);
        setComment('');
    };

    const handleStatusChange = (status: Status) => {
        const newEvent: TimelineEvent = {
            id: `E${Date.now()}`,
            userId: user.id,
            userName: user.name,
            type: 'STATUS_CHANGE',
            content: `Estado cambiado a: ${getStatusLabel(status)}`,
            timestamp: new Date(),
        };

        const updatedTicket = {
            ...ticket,
            status,
            timeline: [...ticket.timeline, newEvent],
            updatedAt: new Date(),
        };

        onUpdate(updatedTicket);
        setNewStatus(status);
    };

    const handleAssign = () => {
        if (!assignee) return;

        const assignedUser = users.find(u => u.id === assignee);
        if (!assignedUser) return;

        const newEvent: TimelineEvent = {
            id: `E${Date.now()}`,
            userId: user.id,
            userName: user.name,
            type: 'ASSIGNMENT',
            content: `Asignado a ${assignedUser.name}`,
            timestamp: new Date(),
        };

        const updatedTicket = {
            ...ticket,
            assignedTo: assignee,
            assignedToName: assignedUser.name,
            status: ticket.status === 'OPEN' ? 'IN_PROGRESS' : ticket.status,
            timeline: [...ticket.timeline, newEvent],
            updatedAt: new Date(),
        };

        onUpdate(updatedTicket);
    };

    return (
        <div className="flex flex-col h-full bg-gray-50">
            <div className="bg-white border-b px-6 py-4 shadow-sm">
                <div className="flex items-center text-sm text-gray-500 mb-2">
                    <button onClick={onBack} className="flex items-center hover:text-black mr-2 transition-colors">
                        <ChevronLeft size={16} className="mr-1" /> Volver
                    </button>
                    <span className="mx-2">/</span>
                    <span>{ticket.siteName}</span>
                    <span className="mx-2">/</span>
                    <span className="mx-2">/</span>
                    <span>{ticket.ticketNumber || ticket.id}</span>
                </div>
                <div className="flex justify-between items-start">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900 mb-2">{ticket.category} - {ticket.subcategory}</h1>
                        <div className="flex items-center space-x-2">
                            <span className={`px-2 py-0.5 rounded text-xs font-bold ${getPriorityColor(ticket.priority)}`}>{ticket.priority}</span>
                            <span className={`px-2 py-0.5 rounded text-xs font-bold border ${getStatusColor(ticket.status)}`}>{getStatusLabel(ticket.status)}</span>
                            <span className={`px-2 py-0.5 rounded text-xs font-bold ${getSLAColor(slaStatus)}`}>
                                SLA: {formatSLATime(ticket.slaDue)}
                            </span>
                        </div>
                    </div>
                    <div className="flex items-center space-x-3">
                        {/* QR Code button for store users */}
                        {isOwner && ticket.status !== 'CLOSED' && (
                            <Button
                                onClick={() => setShowQRGenerator(true)}
                                className="bg-blue-600 hover:bg-blue-700 border-blue-700 text-white shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all uppercase tracking-wide px-6"
                                size="md"
                            >
                                <QrCode size={20} className="mr-2" />
                                <span className="font-bold">GENERAR CÓDIGO QR</span>
                            </Button>
                        )}

                        {/* Archive/Close Button for Active Tickets */}
                        {canDelete && ticket.status !== 'CLOSED' && ticket.status !== 'RESOLVED' && onArchive && (
                            <Button
                                onClick={() => onArchive(ticket.id)}
                                variant="secondary"
                                size="md"
                                className="bg-amber-100 text-amber-900 border-amber-200 hover:bg-amber-200 font-semibold"
                            >
                                <Archive size={18} className="mr-2" /> Cerrar / Archivar
                            </Button>
                        )}

                        {/* Hard Delete for already closed tickets */}
                        {canDelete && (ticket.status === 'CLOSED' || ticket.status === 'RESOLVED') && (
                            <Button
                                onClick={() => onDelete(ticket.id)}
                                variant="danger"
                                size="md"
                                className="font-bold shadow-md shadow-red-200 uppercase tracking-wide"
                            >
                                <Trash2 size={18} className="mr-2" /> ELIMINAR TICKET
                            </Button>
                        )}
                    </div>
                </div>
            </div>

            <div className="flex flex-1 overflow-hidden">
                <div className="flex-1 flex flex-col overflow-y-auto p-6">
                    <div className="flex space-x-6 border-b mb-6">
                        <button onClick={() => setActiveTab('TIMELINE')} className={`pb-3 text-sm font-medium transition-colors ${activeTab === 'TIMELINE' ? 'text-black border-b-2  border-black' : 'text-gray-500 hover:text-gray-700'}`}>Bitácora</button>
                        <button onClick={() => setActiveTab('DETAILS')} className={`pb-3 text-sm font-medium transition-colors ${activeTab === 'DETAILS' ? 'text-black border-b-2 border-black' : 'text-gray-500 hover:text-gray-700'}`}>Detalles</button>
                        <button onClick={() => setActiveTab('ATTACHMENTS')} className={`pb-3 text-sm font-medium transition-colors ${activeTab === 'ATTACHMENTS' ? 'text-black border-b-2 border-black' : 'text-gray-500 hover:text-gray-700'}`}>Adjuntos ({attachments.length})</button>
                        {canEdit && <button onClick={() => setActiveTab('ACTIONS')} className={`pb-3 text-sm font-medium transition-colors ${activeTab === 'ACTIONS' ? 'text-black border-b-2 border-black' : 'text-gray-500 hover:text-gray-700'}`}>Acciones</button>}
                    </div>

                    {activeTab === 'TIMELINE' && (
                        <div className="space-y-6 relative pl-4">
                            {ticket.timeline.map((event, idx) => (
                                <div key={idx} className="relative pl-8">
                                    <div className={`absolute left-0 top-0 w-8 h-8 rounded-full border-2 border-white shadow-sm flex items-center justify-center text-xs font-bold ${event.type === 'SYSTEM' ? 'bg-gray-200 text-gray-600' : 'bg-blue-600 text-white'}`}>
                                        {event.userName.charAt(0)}
                                    </div>
                                    <div className="bg-white p-4 rounded-lg border shadow-sm">
                                        <div className="flex justify-between items-center mb-2">
                                            <span className="font-bold text-sm">{event.userName}</span>
                                            <span className="text-xs text-gray-400">{getRelativeTime(event.timestamp)}</span>
                                        </div>
                                        <p className="text-sm text-gray-700">{event.content}</p>
                                        {event.metadata?.signatureUrl && (
                                            <div className="mt-3 border rounded-lg p-2 bg-gray-50 max-w-xs">
                                                <p className="text-xs text-gray-500 mb-1 font-medium">Firma de Recepción (Cliente):</p>
                                                <img
                                                    src={event.metadata.signatureUrl}
                                                    alt="Firma del Cliente"
                                                    className="w-full h-auto bg-white border border-gray-200 rounded"
                                                />
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}

                            <div className="relative pl-8 mt-6">
                                <div className="bg-white border rounded-lg shadow-sm p-3">
                                    <textarea
                                        className="w-full text-sm resize-none outline-none"
                                        placeholder="Escribe un comentario o actualización..."
                                        rows={3}
                                        value={comment}
                                        onChange={(e) => setComment(e.target.value)}
                                    ></textarea>
                                    <div className="flex justify-between items-center mt-2 pt-2 border-t">
                                        <Button onClick={handleAddComment} disabled={!comment.trim()}>
                                            <Send size={14} className="mr-2" /> Comentar
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {activeTab === 'DETAILS' && (
                        <div className="bg-white p-6 rounded-lg border shadow-sm space-y-4">
                            <div>
                                <h3 className="font-bold text-gray-700 mb-2">Descripción</h3>
                                <p className="text-gray-800">{ticket.description}</p>
                            </div>
                            <div className="grid grid-cols-2 gap-4 pt-4 border-t">
                                <div>
                                    <p className="text-xs text-gray-500 font-bold uppercase mb-1">Tipo</p>
                                    <p className="text-sm">{ticket.type === 'INCIDENT' ? 'Incidente' : 'Solicitud'}</p>
                                </div>
                                <div>
                                    <p className="text-xs text-gray-500 font-bold uppercase mb-1">Creado</p>
                                    <p className="text-sm">{getRelativeTime(ticket.createdAt)}</p>
                                </div>
                                <div>
                                    <p className="text-xs text-gray-500 font-bold uppercase mb-1">Impacto</p>
                                    <p className="text-sm">{ticket.impact || 'No especificado'}</p>
                                </div>
                                <div>
                                    <p className="text-xs text-gray-500 font-bold uppercase mb-1">Urgencia</p>
                                    <p className="text-sm">{ticket.urgency || 'No especificado'}</p>
                                </div>
                            </div>
                        </div>
                    )}

                    {activeTab === 'ATTACHMENTS' && (
                        <div className="bg-white p-6 rounded-lg border shadow-sm">
                            <h3 className="font-bold text-gray-900 mb-4">Archivos Adjuntos</h3>
                            <FileUpload
                                ticketId={ticket.id}
                                userId={user.id}
                                attachments={attachments}
                                onUploadSuccess={(attachment) => {
                                    setAttachments([attachment, ...attachments]);
                                }}
                                onDeleteSuccess={(attachmentId) => {
                                    setAttachments(attachments.filter(a => a.id !== attachmentId));
                                }}
                                onError={(message) => {
                                    console.error('Attachment error:', message);
                                }}
                            />
                        </div>
                    )}

                    {activeTab === 'ACTIONS' && canEdit && (
                        <div className="space-y-6">
                            <div className="bg-white p-6 rounded-lg border shadow-sm">
                                <h3 className="font-bold text-gray-900 mb-4">Cambiar Estado</h3>
                                <div className="grid grid-cols-2 gap-3">
                                    {(['OPEN', 'IN_PROGRESS', 'IN_TRANSIT', 'ON_SITE', 'WAITING_PARTS', 'WAITING_INFO', 'RESOLVED', 'CLOSED'] as Status[]).map(status => {
                                        const isCurrent = status === ticket.status;
                                        const statusColorClass = getStatusColor(status);
                                        // Simple mapping for border/text color based on status color utility
                                        // Assuming getStatusColor returns classes like "bg-blue-100 text-blue-800"
                                        // We will extract the base color for border/hover if possible, or just use a generic style enhanced by the label color.

                                        return (
                                            <button
                                                key={status}
                                                onClick={() => handleStatusChange(status)}
                                                disabled={isCurrent}
                                                className={`
                                                    relative p-4 rounded-xl border-2 transition-all duration-200 flex flex-col items-center justify-center gap-2
                                                    ${isCurrent
                                                        ? 'bg-gray-50 border-gray-200 opacity-50 cursor-not-allowed'
                                                        : 'bg-white hover:shadow-md hover:-translate-y-1 hover:border-blue-500 border-gray-100'
                                                    }
                                                `}
                                            >
                                                <div className={`
                                                    p-2 rounded-full 
                                                    ${isCurrent ? 'bg-gray-200 text-gray-500' : statusColorClass.replace('text-', 'bg-opacity-20 bg-').replace('border-', '')}
                                                `}>
                                                    {/* We can use a simple switch or map for icons if we imported them, 
                                                        or just rely on the label styling for now. 
                                                        For "More flashy", let's use the status color class on the text.
                                                    */}
                                                    <span className={`font-bold ${isCurrent ? 'text-gray-500' : statusColorClass.split(' ')[1]}`}>
                                                        {getStatusLabel(status)}
                                                    </span>
                                                </div>
                                                {isCurrent && <span className="text-xs font-semibold text-gray-400 absolute top-2 right-2">Actual</span>}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>

                            {isHelpdesk && (
                                <div className="bg-white p-6 rounded-lg border shadow-sm">
                                    <h3 className="font-bold text-gray-900 mb-4">Asignar Técnico</h3>
                                    <div className="flex space-x-3">
                                        <select
                                            className="flex-1 border rounded-lg p-2 text-sm"
                                            value={assignee}
                                            onChange={(e) => setAssignee(e.target.value)}
                                        >
                                            <option value="">Sin asignar</option>
                                            {techUsers.map(tech => (
                                                <option key={tech.id} value={tech.id}>{tech.name}</option>
                                            ))}
                                        </select>
                                        <Button onClick={handleAssign} disabled={!assignee}>
                                            Asignar
                                        </Button>
                                    </div>
                                    {ticket.assignedToName && (
                                        <p className="text-sm text-gray-600 mt-2">
                                            Actualmente asignado a: <span className="font-bold">{ticket.assignedToName}</span>
                                        </p>
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                </div>

                <div className="w-80 bg-white border-l p-6 overflow-y-auto hidden md:block">
                    <h3 className="font-bold text-gray-900 mb-4 text-sm uppercase tracking-wider">Detalles del Sitio</h3>
                    <div className="bg-gray-50 p-4 rounded-lg border mb-6">
                        <div className="flex items-start mb-2">
                            <MapPin size={16} className="mt-0.5 mr-2 text-gray-400" />
                            <div>
                                <p className="font-bold text-sm">{ticket.siteName}</p>
                                <p className="text-xs text-gray-500">Región RM</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* QR Generator Modal */}
            {showQRGenerator && (
                <QRGenerator
                    ticketId={ticket.id}
                    userId={user.id}
                    ticketNumber={ticket.ticketNumber || ticket.id}
                    onClose={() => setShowQRGenerator(false)}
                />
            )}
        </div >
    );
};
